import { RiDeleteBinLine, RiEdit2Line, RiGroupLine } from "@remixicon/react";
import React, {
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    GetUserGroupDocument,
    GetUserGroupsDocument,
    ListUserGroupsDocument,
    UserGroup,
    useDeleteUserGroupMutation,
    useGetUserGroupQuery,
    useGetUserGroupsQuery,
    useSaveUserGroupMutation,
} from "../../../../__generated__/api";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import AgentPicker from "../../../../components/pickers/agents/Agents";
import Badge from "../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../components/theme/drawer";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import TableContainer from "../../../../components/theme/table";
import { ReactComponent as GroupsList } from "../../../../assets/icons/groups-list.svg";

type UserGroupWithContact = UserGroup & { contacts: string | undefined }
const UserGroupTableContainer = TableContainer<UserGroupWithContact>();

export function useGroupView() {
    const [id, setGroupId] = useState<string | undefined>(undefined);
    const { data, loading: isLoading } = useGetUserGroupQuery({
        skip: !id,
        variables: {
            id,
        },
    });
    const [save] = useSaveUserGroupMutation();

    const { element, setOpen, formTracker } = useDrawerForm({
        title: id ? "Edit Group" : "Add Group",
        isLoading,
        onHide: () => {
            setGroupId(undefined);
            formTracker.reset();
        },
        onSave: async () => {
            await save({
                refetchQueries: [
                    { query: ListUserGroupsDocument },
                    { query: GetUserGroupsDocument },
                    { query: GetUserGroupDocument, variables: { id } },
                ],
                variables: {
                    id: id !== "new" ? id : undefined,
                    userGroup: {
                        name: formTracker.properties.name.value,
                        description: formTracker.properties.description.value,
                        userIds: formTracker.properties.agents.value,
                    },
                },
            });
            setGroupId(undefined);
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .property("name", () => data?.userGroup?.name ?? "")
                .property("description", () => data?.userGroup?.description ?? "")
                .array("agents", () => data?.userGroup?.userIds ?? []),
            dependencies: [data?.userGroup],
        },
        body: tracker => (
            <>
                <FormGroup
                    label="Group Name"
                    size="medium"
                    required
                    value={tracker.properties.name.value}
                    onChange={evt => tracker.properties.name.setValue(evt.target.value)}
                />
                <FormGroup
                    label="Description"
                    size="medium"
                    display="textarea"
                    isOptional
                    value={tracker.properties.description.value}
                    onChange={evt => tracker.properties.description.setValue(evt.target.value)}
                />
                <FormGroup label="Users">
                    <AgentPicker
                        isMulti
                        value={tracker.properties.agents.value}
                        onSelected={selected => tracker.properties.agents.setValue(selected)}
                    />
                </FormGroup>
            </>
        ),
    });

    useEffect(() => {
        if (id) {
            setOpen(true);
        }
    }, [id]);

    return { element, setOpen, setGroupId };
}

export default function GroupsTable() {
    const { data, error, loading: isLoading } = useGetUserGroupsQuery();

    const [deleteUserGroup] = useDeleteUserGroupMutation();

    const groupData = useMemo(() => data?.userGroups?.map(group => ({
        ...group,
        contacts: group.users.map(row => row.name).join(", "),
    }) as UserGroupWithContact), [data]);

    const { element: deleteElement, setDeleting } = useDeleteConfirm<UserGroupWithContact>(
        async group => {
            await deleteUserGroup({
                refetchQueries: [
                    { query: ListUserGroupsDocument },
                    { query: GetUserGroupsDocument },
                    { query: GetUserGroupDocument },
                ],
                variables: {
                    id: group.id,
                },
            });
        },
        user => user.name,
        "Remove User Group",
        "When deleting a user group, the group is deleted but none of the users are deleted. Would you like to delete",
        "Remove",
    );

    const { element: drawerElement, setOpen, setGroupId } = useGroupView();

    return (
        <>
            <UserGroupTableContainer data={groupData} isLoading={isLoading} error={error}>
                <UserGroupTableContainer.Empty>
                    <UserGroupTableContainer.Empty.Default
                        text="User groups help you organize your users and user permissions."
                        title="Create an group"
                        icon={<GroupsList />}
                        onClick={() => setOpen(true)}
                        buttonText="Add group"
                    />
                </UserGroupTableContainer.Empty>
                <UserGroupTableContainer.TableControls>

                    <UserGroupTableContainer.TableControls.Search
                        search={(query, group) => group.name?.toLocaleLowerCase().includes(query)
                            || group.description?.toLocaleLowerCase().includes(query)
                            || group.contacts?.toLocaleLowerCase().includes(query)
                            || group.users?.length === parseInt(query, 10)}
                    />
                    <UserGroupTableContainer.TableControls.Seperator />
                    <UserGroupTableContainer.TableControls.SortBy />
                    <UserGroupTableContainer.TableControls.Create text="Add Group" onClick={() => setOpen(true)} />
                </UserGroupTableContainer.TableControls>
                <UserGroupTableContainer.Table>
                    <UserGroupTableContainer.TableValue
                        columnKey="name"
                        title="Group Name"
                        sortBy={item => item.name?.toLowerCase()}
                    >
                        {item => item.name}
                    </UserGroupTableContainer.TableValue>
                    <UserGroupTableContainer.TableValue
                        columnKey="description"
                        title="Description"
                        sortBy={item => item.description?.toLowerCase()}
                    >
                        {item => item.description}
                    </UserGroupTableContainer.TableValue>
                    <UserGroupTableContainer.TableValue
                        columnKey="users"
                        title="Users"
                        sortBy={item => item.users?.length}
                    >
                        {item => (
                            <Badge
                                variant="gray"
                                prefixIcon={<RiGroupLine />}
                            >
                                {item.users?.length}
                            </Badge>
                        )}
                    </UserGroupTableContainer.TableValue>

                    <UserGroupTableContainer.Action
                        icon={<RiEdit2Line />}
                        altText="Edit Group"
                        onClick={group => {
                            setGroupId(group.id);
                        }}
                    />
                    <UserGroupTableContainer.Action
                        icon={<RiDeleteBinLine />}
                        altText="Delete Group"
                        onClick={setDeleting}
                    />

                </UserGroupTableContainer.Table>
            </UserGroupTableContainer>
            {deleteElement}
            {drawerElement}
        </>
    );
}
